import React, { useEffect } from 'react';
import { useLingui } from '@lingui/react/macro';
import { z } from 'zod';

import { Button } from '@rover/kibble/core';
import { Form, useKibbleForm } from '@rover/kibble/forms';
import TextField from '@rover/kibble/forms/fields/TextField';
import { useProviderPetTypes } from '@rover/shared/js/hooks/useProviderPetTypes';
import { getWordCount } from '@rover/utilities/wordCount';

import { getTextAreaSchema } from '../../SitterProfilePage/utils';

export type WhatToExpectFormData = {
  schedule: string;
  environment: string;
  experience: string;
  info: string;
};

export type Props = {
  initialData: WhatToExpectFormData;
  isSubmitting: boolean;
  onSubmit: (data: WhatToExpectFormData) => void;
  minWordCount: number;
  setShowBackModal: (isDirty: boolean) => void;
  submissionErrors?: Record<string, string[] | string>;
  isSignUp: boolean;
};

const WhatToExpectForm = ({
  initialData,
  isSubmitting,
  onSubmit,
  minWordCount,
  setShowBackModal,
  submissionErrors = {},
  isSignUp,
}: Props): JSX.Element => {
  const { t } = useLingui();
  const { offersCatCare, offersDogCare } = useProviderPetTypes();
  const isCatOnly = offersCatCare && !offersDogCare;

  const wordCountValidator = getTextAreaSchema(minWordCount);

  const { configureField, formProps, watch, formState, setError } = useKibbleForm({
    schema: z.object({
      schedule: wordCountValidator,
      environment: wordCountValidator,
      experience: isSignUp ? z.string().optional() : wordCountValidator,
      info: isSignUp ? z.string().optional() : wordCountValidator,
    }),
    defaultValues: initialData,
    onValidSubmit: (data) => {
      // Ensure experience and info are strings (convert undefined to empty string)
      onSubmit({
        ...data,
        experience: data.experience ?? '',
        info: data.info ?? '',
      });
    },
  });

  // notify back button in parent component
  useEffect(() => {
    setShowBackModal(formState.isDirty);
  }, [setShowBackModal, formState]);

  // apply submission errors from API to form fields
  useEffect(() => {
    const apiFieldToFormField: Record<string, keyof WhatToExpectFormData> = {
      availability: 'schedule',
      environment: 'environment',
      dogsExperience: 'experience',
      requestedDogInfo: 'info',
    };

    Object.entries(submissionErrors).forEach(([apiFieldName, errors]) => {
      const formFieldName = apiFieldToFormField[apiFieldName] || apiFieldName;

      if (formFieldName in { schedule: true, environment: true, experience: true, info: true }) {
        const errorMessage = Array.isArray(errors) ? errors.join(' ') : errors;
        setError(formFieldName as keyof WhatToExpectFormData, {
          type: 'server',
          message: errorMessage,
        });
      }
    });
  }, [submissionErrors, setError]);

  return (
    <Form {...formProps}>
      <TextField
        {...configureField('schedule')}
        primaryLabel={t`Schedule`}
        secondaryLabel={t`How does pet care fit into your daily or weekly routine?`}
        variant="textarea"
        requirednessLabel="hide"
        placeholder={
          isCatOnly
            ? t`I'm currently working part-time, so I'll have plenty of time to spend with your cats! I'm available on weekends, and weekdays after 2pm...`
            : t`I'm currently working part-time, so I'll have plenty of time to play with your pups! I'm available for boarding on weekends, and weekdays after 2pm...`
        }
        variantOptions={{
          minHeight: '130px',
          minimumWordCount: minWordCount,
          currentWordCount: formState.errors?.schedule
            ? undefined
            : getWordCount(watch('schedule') || '') || 0,
        }}
      />

      <TextField
        {...configureField('environment')}
        primaryLabel={t`Safety, trust & environment`}
        secondaryLabel={t`How do you care for pets in your home and/or your client's home? This will vary depending on which service you offer.`}
        variant="textarea"
        requirednessLabel="hide"
        placeholder={
          isCatOnly
            ? t`I have a quiet, cat-friendly home with plenty of cozy spots. I keep all windows secured and make sure there are no hazards...`
            : t`I have a fenced yard and two friendly dogs to keep your fur baby company. I can easily crate your dog if needed...`
        }
        variantOptions={{
          minHeight: '130px',
          minimumWordCount: minWordCount,
          currentWordCount: formState.errors?.environment
            ? undefined
            : getWordCount(watch('environment') || '') || 0,
        }}
      />

      {!isSignUp && (
        <TextField
          {...configureField('experience')}
          primaryLabel={t`Describe a typical day`}
          secondaryLabel={t`What fun activities will you do with the pets you watch?`}
          requirednessLabel="hide"
          placeholder={
            isCatOnly
              ? t`I love to keep your cats entertained and comfortable. We'll have plenty of playtime with toys, grooming sessions, and cozy cuddle time.`
              : t`I love to stimulate your dogs physically and mentally. We'll play a bunch of games, check out all the new smells in my neighborhood, and explore a big park near my house.`
          }
          variant="textarea"
          variantOptions={{
            minHeight: '130px',
            minimumWordCount: minWordCount,
            currentWordCount: formState.errors?.experience
              ? undefined
              : getWordCount(watch('experience') || '') || 0,
          }}
        />
      )}

      {!isSignUp && (
        <TextField
          {...configureField('info')}
          primaryLabel={t`Important to know about their pets ahead of time`}
          requirednessLabel="hide"
          placeholder={
            isCatOnly
              ? t`Let me know if your cat has any special needs or behaviors that I can account for. My preference is for cats that...`
              : t`Let me know if your dog has any special needs or behaviors that I can account for. My preference is for breeds that...`
          }
          variant="textarea"
          variantOptions={{
            minHeight: '130px',
            minimumWordCount: minWordCount,
            currentWordCount: formState.errors?.info
              ? undefined
              : getWordCount(watch('info') || '') || 0,
          }}
        />
      )}

      <Button
        webProps={{ type: 'submit' }}
        disabled={isSubmitting}
        loading={isSubmitting}
        variant="primary"
        mt="4x"
        fullWidth
      >
        {t`Save & Continue`}
      </Button>
    </Form>
  );
};

export default WhatToExpectForm;
